import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { MapPin, Save, Home, Package } from 'lucide-react';

const emptyAddr = { name: '', phone: '', street: '', city: '', state: '', country: 'MX', postal_code: '', reference: '' };

const AddressForm = ({ title, value, onChange, icon: Icon, testPrefix }) => (
    <Card>
        <CardContent className="p-4 space-y-2">
            <div className="flex items-center gap-2 pb-2 border-b">
                <Icon className="h-5 w-5 text-[hsl(var(--primary))]" />
                <div className="text-sm font-semibold flex-1">{title}</div>
            </div>
            <div className="grid grid-cols-2 gap-2">
                <div className="col-span-2"><Label className="text-xs">Nombre completo</Label><Input value={value.name || ''} onChange={(e) => onChange({ ...value, name: e.target.value })} data-testid={`${testPrefix}-name`} /></div>
                <div><Label className="text-xs">Teléfono</Label><Input value={value.phone || ''} onChange={(e) => onChange({ ...value, phone: e.target.value })} placeholder="+52 55 1234 5678" data-testid={`${testPrefix}-phone`} /></div>
                <div><Label className="text-xs">Código Postal</Label><Input value={value.postal_code || ''} onChange={(e) => onChange({ ...value, postal_code: e.target.value.replace(/\D/g, '').slice(0, 5) })} data-testid={`${testPrefix}-postal`} /></div>
                <div className="col-span-2"><Label className="text-xs">Calle y número</Label><Input value={value.street || ''} onChange={(e) => onChange({ ...value, street: e.target.value })} data-testid={`${testPrefix}-street`} /></div>
                <div><Label className="text-xs">Ciudad</Label><Input value={value.city || ''} onChange={(e) => onChange({ ...value, city: e.target.value })} data-testid={`${testPrefix}-city`} /></div>
                <div><Label className="text-xs">Estado</Label><Input value={value.state || ''} onChange={(e) => onChange({ ...value, state: e.target.value })} placeholder="NL" data-testid={`${testPrefix}-state`} /></div>
                <div className="col-span-2"><Label className="text-xs">Referencia (opcional)</Label><Input value={value.reference || ''} onChange={(e) => onChange({ ...value, reference: e.target.value })} placeholder="Casa con reja negra, entre calles…" /></div>
            </div>
        </CardContent>
    </Card>
);

export default function DashboardAddresses() {
    const [shipping, setShipping] = useState({ ...emptyAddr });
    const [pickup, setPickup] = useState({ ...emptyAddr });
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get('/me/addresses').then(({ data }) => {
            if (data?.default_shipping_address) setShipping({ ...emptyAddr, ...data.default_shipping_address });
            if (data?.default_pickup_address) setPickup({ ...emptyAddr, ...data.default_pickup_address });
        }).catch(() => {}).finally(() => setLoading(false));
    }, []);

    const save = async () => {
        setBusy(true);
        try {
            await api.put('/me/addresses', { default_shipping_address: shipping, default_pickup_address: pickup });
            toast.success('Direcciones guardadas');
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); } finally { setBusy(false); }
    };

    if (loading) return <div className="text-sm text-muted-foreground py-10 text-center">Cargando…</div>;

    return (
        <div className="space-y-4" data-testid="dashboard-addresses">
            <div className="flex items-center gap-2">
                <MapPin className="h-6 w-6 text-[hsl(var(--primary))]" />
                <h1 className="text-2xl font-semibold">Mis direcciones</h1>
            </div>
            <p className="text-sm text-muted-foreground">
                Se usan para autollenar cotizaciones de OkEnvíos al comprar (dirección de envío) o al generar guías como vendedor (origen).
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <AddressForm title="Dirección de envío (compras)" value={shipping} onChange={setShipping} icon={Home} testPrefix="shipping" />
                <AddressForm title="Dirección de origen (ventas)" value={pickup} onChange={setPickup} icon={Package} testPrefix="pickup" />
            </div>
            <div className="flex justify-end">
                <Button onClick={save} disabled={busy} data-testid="save-addresses"><Save className="h-4 w-4 mr-1" />{busy ? 'Guardando…' : 'Guardar direcciones'}</Button>
            </div>
        </div>
    );
}
